import React, { useState } from "react";
import {
  Card,
  Stack,
  Group,
  Text,
  TextInput,
  Select,
  NumberInput,
  Button,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";

const EXPENSE_TYPE_OPTIONS = [
  { value: "BACKEND_PERCENTAGE", label: "Backend Percentage" },
  { value: "BACKEND_FIXED_AMOUNT", label: "Backend Fixed Amount" },
  { value: "FRONTEND_RECURRING", label: "Frontend Recurring" },
];

const EXPENSE_TYPE_LABEL_BY_VALUE = EXPENSE_TYPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

// Pull the type value off a ps_company_expense_types entry, tolerating the few
// keys the overview may use.
const getCompanyExpenseType = entry =>
  entry?.type ?? entry?.expense_type_category ?? entry?.expense_type ?? null;

const isCompanyTypeActive = entry =>
  entry?.is_active !== false && entry?.active !== false;

const RECURRING_SCHEDULE_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "MONTHLY", label: "Monthly" },
];

// The API stores the day of week as 1–7 starting on Sunday (the same encoding
// the bonus form and the deal review use), not as a name.
const DAY_OF_WEEK_OPTIONS = [
  { value: "1", label: "Sunday" },
  { value: "2", label: "Monday" },
  { value: "3", label: "Tuesday" },
  { value: "4", label: "Wednesday" },
  { value: "5", label: "Thursday" },
  { value: "6", label: "Friday" },
  { value: "7", label: "Saturday" },
];

const MONTH_DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

// Form for adding an expense. In `inline` mode it builds the expense object in
// the deal payload shape and hands it back through onCreated without an API
// call; otherwise it saves a new default expense via the API. When
// `companyExpenseTypes` is provided, the type options come from the company's
// active expense types.
function AddExpenseForm({
  sellingYearId,
  onCreated,
  onCancel,
  inline = false,
  companyExpenseTypes = null,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();

  const expenseTypeOptions = Array.isArray(companyExpenseTypes)
    ? companyExpenseTypes
        .filter(isCompanyTypeActive)
        .map(entry => {
          const value = getCompanyExpenseType(entry);
          return value
            ? { value, label: EXPENSE_TYPE_LABEL_BY_VALUE[value] ?? value }
            : null;
        })
        .filter(Boolean)
    : EXPENSE_TYPE_OPTIONS;

  const [expenseType, setExpenseType] = useState(null);
  const [name, setName] = useState("");
  const [backendPercentage, setBackendPercentage] = useState("");
  const [backendFixedAmount, setBackendFixedAmount] = useState("");
  const [recurringFrontendAmount, setRecurringFrontendAmount] = useState("");
  const [recurringScheduleType, setRecurringScheduleType] = useState(null);
  const [recurringDayOfWeek, setRecurringDayOfWeek] = useState(null);
  const [recurringDayOfMonth, setRecurringDayOfMonth] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    if (!expenseType)
      return (showError("Please select an expense type"), false);
    if (!name.trim()) return (showError("Please enter a name"), false);
    if (expenseType === "BACKEND_PERCENTAGE" && backendPercentage === "")
      return (showError("Please enter a backend percentage"), false);
    if (expenseType === "BACKEND_FIXED_AMOUNT" && backendFixedAmount === "")
      return (showError("Please enter a backend fixed amount"), false);
    if (expenseType === "FRONTEND_RECURRING") {
      if (recurringFrontendAmount === "")
        return (showError("Please enter a recurring frontend amount"), false);
      if (!recurringScheduleType)
        return (showError("Please select a recurring schedule type"), false);
      if (recurringScheduleType === "MONTHLY" && !recurringDayOfMonth)
        return (showError("Please select a day of the month"), false);
      if (recurringScheduleType !== "MONTHLY" && !recurringDayOfWeek)
        return (showError("Please select a day of the week"), false);
    }
    return true;
  };

  const buildPayload = () => {
    const payload = {
      expense_type: expenseType,
      name: name.trim(),
      selling_year_id: sellingYearId,
    };
    if (expenseType === "BACKEND_PERCENTAGE") {
      payload.backend_percentage = Number(backendPercentage);
    } else if (expenseType === "BACKEND_FIXED_AMOUNT") {
      payload.backend_fixed_amount = Number(backendFixedAmount);
    } else if (expenseType === "FRONTEND_RECURRING") {
      payload.recurring_frontend_amount = Number(recurringFrontendAmount);
      payload.recurring_schedule_type = recurringScheduleType;
      if (recurringScheduleType === "MONTHLY") {
        payload.recurring_day_of_month = Number(recurringDayOfMonth);
      } else {
        payload.recurring_day_of_week = Number(recurringDayOfWeek);
      }
    }
    return payload;
  };

  // The expense in the deal payload shape (matches buildExpensePayload /
  // ExpenseDisplay), built straight from the form for the inline flow.
  const buildInlineExpense = () => {
    const expense = {
      name: name.trim(),
      expense_type_category: expenseType,
      backend_percentage: null,
      backend_fixed_amount: null,
      recurring_frontend_amount: null,
      recurring_schedule_type: null,
      recurring_day_of_week: null,
      recurring_day_of_month: null,
    };
    if (expenseType === "BACKEND_PERCENTAGE") {
      expense.backend_percentage = Number(backendPercentage);
    } else if (expenseType === "BACKEND_FIXED_AMOUNT") {
      expense.backend_fixed_amount = Number(backendFixedAmount);
    } else if (expenseType === "FRONTEND_RECURRING") {
      expense.recurring_frontend_amount = Number(recurringFrontendAmount);
      expense.recurring_schedule_type = recurringScheduleType;
      if (recurringScheduleType === "MONTHLY") {
        expense.recurring_day_of_month = Number(recurringDayOfMonth);
      } else {
        expense.recurring_day_of_week = Number(recurringDayOfWeek);
      }
    }
    return expense;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Inline flow: no API call, just hand the expense back to the wizard.
    if (inline) {
      if (onCreated) onCreated(buildInlineExpense());
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/ps/default-expense"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(buildPayload()),
      });

      if (response.ok) {
        const result = await response.json();
        const created = result?.data || result || {};
        if (onCreated) onCreated(created);
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to create expense"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to create expense");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="md">
        <Text size="sm" fw={600}>
          New Expense
        </Text>

        <Select
          label="Expense Type"
          placeholder="Select expense type"
          data={expenseTypeOptions}
          value={expenseType}
          onChange={setExpenseType}
          required
        />
        <TextInput
          label="Name"
          placeholder="e.g., Equipment Fee"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />

        {expenseType === "BACKEND_PERCENTAGE" && (
          <NumberInput
            label="Backend Percentage"
            placeholder="Enter backend percentage"
            value={backendPercentage}
            onChange={setBackendPercentage}
            min={0}
            suffix="%"
          />
        )}

        {expenseType === "BACKEND_FIXED_AMOUNT" && (
          <NumberInput
            label="Backend Fixed Amount"
            placeholder="Enter backend fixed amount"
            value={backendFixedAmount}
            onChange={setBackendFixedAmount}
            min={0}
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
          />
        )}

        {expenseType === "FRONTEND_RECURRING" && (
          <>
            <NumberInput
              label="Recurring Frontend Amount"
              placeholder="Enter recurring amount"
              value={recurringFrontendAmount}
              onChange={setRecurringFrontendAmount}
              min={0}
              prefix="$"
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
            />
            <Select
              label="Recurring Schedule Type"
              placeholder="Select schedule"
              data={RECURRING_SCHEDULE_OPTIONS}
              value={recurringScheduleType}
              onChange={setRecurringScheduleType}
            />
            {recurringScheduleType === "MONTHLY" ? (
              <Select
                label="Day of Month"
                placeholder="Select day of month"
                data={MONTH_DAY_OPTIONS}
                value={recurringDayOfMonth}
                onChange={setRecurringDayOfMonth}
                searchable
              />
            ) : (
              recurringScheduleType && (
                <Select
                  label="Day of Week"
                  placeholder="Select day of week"
                  data={DAY_OF_WEEK_OPTIONS}
                  value={recurringDayOfWeek}
                  onChange={setRecurringDayOfWeek}
                />
              )
            )}
          </>
        )}

        <Group justify="flex-end">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} loading={submitting}>
            {inline ? "Add Expense" : "Save Expense"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export default AddExpenseForm;
